/**
 * QuickMenu — спливає під токеном на одинарний клік. Дві варіації:
 *  - GmQuickMenu — повний набір дій майстра.
 *  - PlayerQuickMenu — обмежений набір; розрізняє свій vs чужий токен.
 *
 * Дизайн: горизонтальна смуга кнопок-іконок, без емодзі (правило #2).
 * Кольорові крапки conditions беруться з conditions.ts.
 */
import type { Combatant, CombatantPublicView, ConditionType } from '@sd/shared';
import { CONDITIONS } from './conditions.js';

type AnyToken = (Combatant | CombatantPublicView) & { stats?: Combatant['stats'] };

interface CommonProps {
  token: AnyToken;
  close: () => void;
}

// ── Reusable icon-button ────────────────────────────────────────────────
function MenuBtn({ label, title, danger, on, onClick }: {
  label: string;
  title?: string;
  danger?: boolean;
  on?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`qm-btn ${danger ? 'danger' : ''} ${on ? 'on' : ''}`}
      title={title ?? label}
    >
      {label}
    </button>
  );
}

function ConditionRow({ active, onToggle }: {
  active: ConditionType[];
  onToggle: (c: ConditionType, on: boolean) => void;
}): JSX.Element {
  return (
    <div className="qm-conds">
      {CONDITIONS.map((c) => {
        const isOn = active.includes(c.type);
        return (
          <button
            key={c.type}
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(c.type, !isOn); }}
            className={`qm-cond ${isOn ? 'on' : ''}`}
            title={c.label}
            style={{ ['--cd-color' as string]: c.color }}
          >
            <span className="qm-cond-dot" />
            <span className="qm-cond-lbl">{c.short}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── GM ──────────────────────────────────────────────────────────────────
export function GmQuickMenu({
  token, close,
  isTarget, onTarget, onInfo, onConditionToggle, onDelete,
}: CommonProps & {
  isTarget: boolean;
  onTarget: () => void;
  onInfo: () => void;
  onConditionToggle: (cond: ConditionType, on: boolean) => void;
  onDelete: () => void;
}): JSX.Element {
  return (
    <div className="qm-card" onPointerDown={(e) => e.stopPropagation()}>
      <div className="qm-head">
        <span className="qm-name">{token.name}</span>
        <button type="button" className="qm-x" onClick={close} title="Закрити">×</button>
      </div>
      <div className="qm-row">
        <MenuBtn
          label={isTarget ? 'Зняти ціль' : 'Ціль'}
          title="Зробити ціллю (toggle)"
          on={isTarget}
          onClick={() => { onTarget(); close(); }}
        />
        <MenuBtn label="Інфо" title="Відкрити detail-картку" onClick={() => { onInfo(); close(); }} />
        <MenuBtn
          label="Видалити"
          title="Видалити з зустрічі"
          danger
          onClick={() => { onDelete(); close(); }}
        />
      </div>
      <ConditionRow active={token.conditions} onToggle={onConditionToggle} />
    </div>
  );
}

// ── Player ──────────────────────────────────────────────────────────────
export function PlayerQuickMenu({
  token, close,
  isOwn, isTarget, onTarget, onConditionToggle, onActions, onFeatures,
}: CommonProps & {
  isOwn: boolean;
  isTarget: boolean;
  onTarget: () => void;
  onConditionToggle: (cond: ConditionType, on: boolean) => void;
  onActions: () => void;
  onFeatures: () => void;
}): JSX.Element {
  return (
    <div className="qm-card" onPointerDown={(e) => e.stopPropagation()}>
      <div className="qm-head">
        <span className="qm-name">{token.name}</span>
        <button type="button" className="qm-x" onClick={close} title="Закрити">×</button>
      </div>
      <div className="qm-row">
        {!isOwn && (
          <MenuBtn
            label={isTarget ? 'Зняти ціль' : 'Ціль'}
            on={isTarget}
            onClick={() => { onTarget(); close(); }}
          />
        )}
        {isOwn && (
          <>
            <MenuBtn label="Дії" title="Відкрити вкладку Дії" onClick={() => { onActions(); close(); }} />
            <MenuBtn label="Здібності" title="Відкрити вкладку Здібності" onClick={() => { onFeatures(); close(); }} />
          </>
        )}
      </div>
      {isOwn && <ConditionRow active={token.conditions} onToggle={onConditionToggle} />}
    </div>
  );
}
